import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { employeeApi, locationApi, positionApi, scheduleApi, payrollDepartmentApi, roleApi, employeeLicenseApi, payRecordApi, feedbackRecordApi, trainingRecordApi, trainingTypeApi, paymentTypeApi, paymentFrequencyApi, assignLaborCostByOptionApi, workingDayApi, employeeNoteApi, leavePoliciesApi, employeeLeaveBalanceApi } from '../services/api';
import { Employee, Location, Position, Schedule, PayrollDepartment, Role, UpdateEmployeeRequest, EmployeeLicense, PayRecord, FeedbackRecord, TrainingRecord, TrainingType, PaymentType, PaymentFrequency, AssignLaborCostByOption, WorkingDay, CreateEmployeeLicenseRequest, CreatePayRecordRequest, CreateFeedbackRecordRequest, CreateTrainingRecordRequest, EmployeeNote, CreateEmployeeNoteRequest, UpdateEmployeeNoteRequest, LeavePolicy, EmployeeLeaveBalance } from '../types';
import { ArrowLeft, Save, User, Lock, Briefcase, FileText, Award, Currency, Receipt, MessageSquare, GraduationCap, Plus, Edit, Trash2, X, Paperclip, Loader2, Calendar } from 'lucide-react';
import BasketSelect from '../components/BasketSelect';
import { useToast, ToastContainer } from '../components/Toast';
import PayrollAndPayRecords from '../components/PayrollAndPayRecords';

const EmployeeProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeTabParam = searchParams.get('tab') || 'login';

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [payrollDepartments, setPayrollDepartments] = useState<PayrollDepartment[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);
  const [licenses, setLicenses] = useState<EmployeeLicense[]>([]);
  const [payRecords, setPayRecords] = useState<PayRecord[]>([]);
  const [feedbackRecords, setFeedbackRecords] = useState<FeedbackRecord[]>([]);
  const [trainingRecords, setTrainingRecords] = useState<TrainingRecord[]>([]);
  const [employeeNotes, setEmployeeNotes] = useState<EmployeeNote[]>([]);
  const [leavePolicies, setLeavePolicies] = useState<LeavePolicy[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<EmployeeLeaveBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(activeTabParam);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const [formData, setFormData] = useState<UpdateEmployeeRequest>({
    email: '',
    firstName: '',
    lastName: '',
    pin: '',
    isActive: true,
    subLocationIds: [],
    subPositionIds: [],
    scheduleIds: [],
    appRoleIds: [],
  });
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setActiveTab(activeTabParam);
  }, [activeTabParam]);

  // Scroll to visa details when tab is personal and hash/param is present
  useEffect(() => {
    if (loading) return; // Don't scroll while loading
    
    const shouldScroll = activeTab === 'personal' && 
      (window.location.hash === '#visa-details' || searchParams.get('scrollToVisa') === 'true');
    
    if (!shouldScroll) return;
    
    // Use a more robust scrolling approach with multiple retries
    let retryCount = 0;
    const maxRetries = 30; // Try for up to 3 seconds (30 * 100ms)
    
    const attemptScroll = () => {
      // Check if the form with personal tab is actually rendered
      const formElement = document.querySelector('form');
      const visaSection = document.getElementById('visa-details');
      
      if (visaSection && formElement) {
        // Element found - scroll to it using scrollIntoView (more reliable)
        try {
          visaSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          
          // Also adjust position slightly to account for header
          setTimeout(() => {
            const currentPosition = window.pageYOffset;
            const targetPosition = Math.max(0, currentPosition - 100);
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }, 300);
          
          console.log('[EmployeeProfile] Successfully scrolled to visa-details section');
          return; // Success - stop retrying
        } catch (error) {
          console.warn('[EmployeeProfile] Error scrolling:', error);
          // Fallback: try simple scroll
          const elementPosition = visaSection.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo(0, Math.max(0, elementPosition - 100));
          return;
        }
      } else {
        // Element not found yet - retry
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(attemptScroll, 100);
        } else {
          console.warn('[EmployeeProfile] Could not find visa-details element after', maxRetries, 'retries. Form exists:', !!formElement, 'Visa section exists:', !!visaSection);
        }
      }
    };
    
    // Start attempting to scroll after a longer delay to ensure form is rendered
    setTimeout(() => {
      attemptScroll();
    }, 500);
  }, [activeTab, searchParams, loading]); // Include loading in dependencies

  const fetchData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const employeeId = parseInt(id);
      const currentYear = new Date().getFullYear();
      const [employeeData, locationsData, positionsData, schedulesData, payrollDepartmentsData, rolesData, trainingTypesData, licensesData, payRecordsData, feedbackRecordsData, trainingRecordsData, employeeNotesData, leavePoliciesData] = await Promise.all([
        employeeApi.getById(employeeId),
        locationApi.getAll(),
        positionApi.getAll(),
        scheduleApi.getAll(),
        payrollDepartmentApi.getAll(),
        roleApi.getAll(),
        trainingTypeApi.getAll(),
        employeeLicenseApi.getByEmployeeId(employeeId),
        payRecordApi.getByEmployeeId(employeeId),
        feedbackRecordApi.getByEmployeeId(employeeId),
        trainingRecordApi.getByEmployeeId(employeeId),
        employeeNoteApi.getByEmployeeId(employeeId),
        leavePoliciesApi.getAll()
      ]);
      
      // Load leave balances separately (might fail if endpoint doesn't exist yet)
      let leaveBalancesData: EmployeeLeaveBalance[] = [];
      try {
        leaveBalancesData = await employeeLeaveBalanceApi.getAll(employeeId, currentYear);
      } catch (error) {
        console.log('Leave balances not available yet:', error);
      }

      setEmployee(employeeData);
      setLocations(locationsData);
      setPositions(positionsData);
      setSchedules(schedulesData);
      setPayrollDepartments(payrollDepartmentsData);
      setRoles(rolesData);
      setTrainingTypes(trainingTypesData);
      setLicenses(licensesData);
      setPayRecords(payRecordsData);
      setFeedbackRecords(feedbackRecordsData);
      setTrainingRecords(trainingRecordsData);
      setEmployeeNotes(employeeNotesData);
      setLeavePolicies(leavePoliciesData);
      setLeaveBalances(leaveBalancesData);

      // Initialize form data
      setFormData({
        email: employeeData.email || '',
        firstName: employeeData.firstName || '',
        lastName: employeeData.lastName || '',
        pin: employeeData.pin || '',
        isActive: employeeData.isActive ?? true,
        password: '',
        passwordQuestion: employeeData.passwordQuestion || '',
        passwordAnswer: employeeData.passwordAnswer || '',
        gender: employeeData.gender || '',
        genderOther: employeeData.genderOther || '',
        birthday: employeeData.birthday ? employeeData.birthday.split('T')[0] : '',
        phone: employeeData.phone || '',
        address: employeeData.address || '',
        psaLicense: employeeData.psaLicense || '',
        startDate: employeeData.startDate ? employeeData.startDate.split('T')[0] : '',
        comment: employeeData.comment || '',
        mustChangePassword: employeeData.mustChangePassword ?? false,
        visaNumber: employeeData.visaNumber || '',
        visaExpiryDate: employeeData.visaExpiryDate ? employeeData.visaExpiryDate.split('T')[0] : '',
        mainLocationId: employeeData.profile?.mainLocationId || undefined,
        mainPositionId: employeeData.profile?.mainPositionId || undefined,
        payrollDepartmentId: employeeData.profile?.payrollDepartmentId || undefined,
        subLocationIds: (employeeData.profile?.subLocationIds && Array.isArray(employeeData.profile.subLocationIds))
          ? employeeData.profile.subLocationIds.map(id => Number(id)).filter(id => !isNaN(id))
          : [],
        subPositionIds: (employeeData.profile?.subPositionIds && Array.isArray(employeeData.profile.subPositionIds))
          ? employeeData.profile.subPositionIds.map(id => Number(id)).filter(id => !isNaN(id))
          : [],
        scheduleIds: (employeeData.profile?.scheduleIds && Array.isArray(employeeData.profile.scheduleIds))
          ? employeeData.profile.scheduleIds.map(id => Number(id)).filter(id => !isNaN(id))
          : [],
        appRoleIds: (employeeData.profile?.appRoleIds && Array.isArray(employeeData.profile.appRoleIds))
          ? employeeData.profile.appRoleIds.map(id => Number(id)).filter(id => !isNaN(id))
          : [],
        allowMobileClockIn: employeeData.profile?.allowMobileClockIn ?? true,
        employmentType: employeeData.profile?.employmentType || '',
        // Leave policy
        leavePolicyId: employeeData.leavePolicyId || undefined,
        // Payroll fields
        worksNumber: employeeData.payroll?.worksNumber || '',
        ppsNumber: employeeData.payroll?.ppsNumber || '',
        ban: employeeData.payroll?.ban || '',
        bic: employeeData.payroll?.bic || '',
        accountNumber: employeeData.payroll?.accountNumber || '',
        sortCode: employeeData.payroll?.sortCode || '',
        paymentType: employeeData.payroll?.paymentType || '',
        hourlyRate: employeeData.payroll?.hourlyRate || undefined,
        sundayRate: employeeData.payroll?.sundayRate || undefined,
        bankHolidayRate: employeeData.payroll?.bankHolidayRate || undefined,
      });
    } catch (error) {
      console.error('Error fetching employee data:', error);
      setError('Failed to load employee data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const parseJsonArray = (value: string | string[] | undefined): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value.map(v => v.toString());
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(v => v.toString()) : [];
    } catch {
      return [];
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setError(null);
    
    // Validate required fields
    if (!formData.mainLocationId) {
      toast.error('Main Location is required.');
      return;
    }
    if (!formData.mainPositionId) {
      toast.error('Main Position is required.');
      return;
    }
    if (!formData.payrollDepartmentId) {
      toast.error('Payroll Department is required.');
      return;
    }
    if (!formData.employmentType || formData.employmentType.trim() === '') {
      toast.error('Employment Type is required.');
      return;
    }
    if (!formData.appRoleIds || formData.appRoleIds.length === 0) {
      toast.error('At least one App Role is required.');
      return;
    }

    setSaving(true);

    try {
      // Only include password fields if password is provided and not empty
      const updateData = { ...formData };
      if (!updateData.password || updateData.password.trim() === '') {
        delete updateData.password;
      }
      
      // Backend requires these flags to be true to process multi-select arrays
      updateData.updateSubLocations = true;
      updateData.updateSubPositions = true;
      updateData.updateSchedules = true;
      updateData.updateAppRoles = true;
      
      await employeeApi.update(parseInt(id), updateData);
      // Reload employee data
      await fetchData();
      toast.success('Employee updated successfully!');
      // Clear password fields after successful update
      setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
    } catch (error: any) {
      console.error('Error updating employee:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          error?.message || 
                          'Failed to update employee. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field: keyof UpdateEmployeeRequest, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // If main location is changed, remove it from sub-locations if it was selected
      if (field === 'mainLocationId' && value) {
        updated.subLocationIds = (updated.subLocationIds || []).filter(id => id !== value);
      }
      
      // If main position is changed, remove it from sub-positions if it was selected
      if (field === 'mainPositionId' && value) {
        updated.subPositionIds = (updated.subPositionIds || []).filter(id => id !== value);
      }
      
      return updated;
    });
  };

  const handleArrayFieldChange = (field: 'subLocationIds' | 'subPositionIds' | 'scheduleIds' | 'appRoleIds', value: string[]) => {
    const numValues = value.map(v => parseInt(v)).filter(v => !isNaN(v));
    setFormData(prev => {
      const updated = { ...prev, [field]: numValues };
      
      // Prevent main location from being in sub-locations
      if (field === 'subLocationIds' && prev.mainLocationId) {
        updated.subLocationIds = numValues.filter(id => id !== prev.mainLocationId);
      }
      
      // Prevent main position from being in sub-positions
      if (field === 'subPositionIds' && prev.mainPositionId) {
        updated.subPositionIds = numValues.filter(id => id !== prev.mainPositionId);
      }
      
      return updated;
    });
  };

  if (loading) {
    return (
      <div className="w-full max-w-[82%] mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div>Loading employee data...</div>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="w-full max-w-[82%] mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Employee not found.</p>
          <button
            onClick={() => navigate('/employees')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Employees
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'login', label: 'Login', icon: Lock },
    { id: 'personal', label: 'Personal', icon: User },
    { id: 'work', label: 'Work', icon: Briefcase },
    { id: 'leave', label: 'Leave', icon: Calendar },
    { id: 'licenses', label: 'Licenses', icon: Award },
    { id: 'payroll', label: 'Payroll & Pay Records', icon: Currency },
    { id: 'feedback', label: 'Feedback', icon: MessageSquare },
    { id: 'training', label: 'Training', icon: GraduationCap },
    { id: 'notes', label: 'Notes', icon: FileText },
  ];

  return (
    <div className="w-full max-w-[82%] mx-auto p-6">
      <ToastContainer toasts={toast.toasts} onClose={toast.removeToast} />
      <div className="mb-6">
        <button
          onClick={() => navigate('/employees')}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Employees
        </button>
        <h1 className="text-2xl md:text-3xl font-bold text-neutral-800">
          {employee.firstName} {employee.lastName}
        </h1>
        <p className="text-neutral-600 mt-1">{employee.email}</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-neutral-200 mb-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  navigate(`/employees/${id}?tab=${tab.id}`, { replace: true });
                }}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'licenses' && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <LicensesTab
            licenses={licenses}
            employeeId={parseInt(id || '0')}
            onRefresh={fetchData}
          />
        </div>
      )}

      {activeTab === 'payroll' && employee && (
        <PayrollAndPayRecords
          employee={employee}
          payRecords={payRecords}
          locations={locations}
          positions={positions}
          schedules={schedules}
          onRefresh={fetchData}
        />
      )}

      {activeTab === 'feedback' && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <FeedbackTab
            feedbackRecords={feedbackRecords}
            employeeId={parseInt(id || '0')}
            onRefresh={fetchData}
          />
        </div>
      )}

      {activeTab === 'training' && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <TrainingTab
            trainingRecords={trainingRecords}
            employeeId={parseInt(id || '0')}
            trainingTypes={trainingTypes}
            onRefresh={fetchData}
          />
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <NotesTab
            employeeNotes={employeeNotes}
            employeeId={parseInt(id || '0')}
            onRefresh={fetchData}
          />
        </div>
      )}

      {activeTab === 'leave' && (
        <div className="space-y-6">
          {/* Leave Policy Assignment */}
          <div className="bg-white border border-neutral-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-neutral-800 mb-4">Leave Policy Assignment</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Assigned Leave Policy
                </label>
                <select
                  value={formData.leavePolicyId || ''}
                  onChange={(e) => handleFieldChange('leavePolicyId', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full md:w-1/2 px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- No Policy Assigned --</option>
                  {leavePolicies.map(policy => (
                    <option key={policy.leavePolicyId} value={policy.leavePolicyId}>
                      {policy.policyName} {policy.isDefault && '(Default)'}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-neutral-500">
                  Select the leave policy that applies to this employee
                </p>
              </div>

              {formData.leavePolicyId && leavePolicies.find(p => p.leavePolicyId === formData.leavePolicyId) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">
                    {leavePolicies.find(p => p.leavePolicyId === formData.leavePolicyId)?.policyName}
                  </h3>
                  <p className="text-sm text-blue-800">
                    {leavePolicies.find(p => p.leavePolicyId === formData.leavePolicyId)?.description || 'No description'}
                  </p>
                  <div className="mt-3 text-xs text-blue-700 space-y-1">
                    <div>Leave Year Starts: {new Date(2024, (leavePolicies.find(p => p.leavePolicyId === formData.leavePolicyId)?.leaveYearStartMonth || 1) - 1, leavePolicies.find(p => p.leavePolicyId === formData.leavePolicyId)?.leaveYearStartDay || 1).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</div>
                    <div>Carry Forward: {leavePolicies.find(p => p.leavePolicyId === formData.leavePolicyId)?.allowCarryForward ? `Yes (up to ${leavePolicies.find(p => p.leavePolicyId === formData.leavePolicyId)?.maxCarryForwardDays || 'unlimited'} days)` : 'No'}</div>
                    <div>Minimum Notice: {leavePolicies.find(p => p.leavePolicyId === formData.leavePolicyId)?.minNoticeRequiredDays || 0} days</div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save Policy Assignment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Leave Balances */}
          <div className="bg-white border border-neutral-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-neutral-800 mb-4">
              Leave Balances ({new Date().getFullYear()})
            </h2>
            
            {leaveBalances.length > 0 ? (
              <div className="space-y-4">
                {leaveBalances.map(balance => (
                  <div key={balance.id} className="border border-neutral-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-neutral-900">
                          {balance.leaveType?.name || 'Unknown Leave Type'}
                        </h3>
                        <p className="text-sm text-neutral-600">
                          {balance.leaveType?.description || ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-600">
                          {balance.remainingDays}
                        </div>
                        <div className="text-xs text-neutral-500">days remaining</div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                      <div>
                        <div className="text-neutral-500">Entitled</div>
                        <div className="font-semibold">{balance.entitledDays} days</div>
                      </div>
                      <div>
                        <div className="text-neutral-500">Accrued</div>
                        <div className="font-semibold">{balance.accruedDays} days</div>
                      </div>
                      <div>
                        <div className="text-neutral-500">Taken</div>
                        <div className="font-semibold text-orange-600">{balance.takenDays} days</div>
                      </div>
                      <div>
                        <div className="text-neutral-500">Pending</div>
                        <div className="font-semibold text-yellow-600">{balance.pendingDays} days</div>
                      </div>
                      <div>
                        <div className="text-neutral-500">Carried Forward</div>
                        <div className="font-semibold text-green-600">{balance.carriedForwardDays} days</div>
                      </div>
                    </div>
                    
                    {balance.nextAccrualDate && (
                      <div className="mt-3 text-xs text-neutral-500">
                        Next accrual: {new Date(balance.nextAccrualDate).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-neutral-500">
                <Calendar className="h-12 w-12 mx-auto mb-3 text-neutral-400" />
                <p>No leave balances found for this employee.</p>
                <p className="text-sm mt-1">
                  {formData.leavePolicyId 
                    ? 'Leave balances will be created when the policy is saved and activated.'
                    : 'Assign a leave policy to this employee to set up leave balances.'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {(activeTab === 'login' || activeTab === 'personal' || activeTab === 'work') && (
        <form onSubmit={handleSubmit} className="bg-white border border-neutral-200 rounded-lg p-6">
        {activeTab === 'login' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-neutral-800 mb-4">Login Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">PIN *</label>
                <input
                  type="text"
                  required
                  maxLength={4}
                  value={formData.pin}
                  onChange={(e) => handleFieldChange('pin', e.target.value.replace(/\D/g, ''))}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">New Password (leave blank to keep current)</label>
                <input
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => handleFieldChange('password', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Password Question</label>
                <input
                  type="text"
                  value={formData.passwordQuestion || ''}
                  onChange={(e) => handleFieldChange('passwordQuestion', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Password Answer</label>
                <input
                  type="text"
                  value={formData.passwordAnswer || ''}
                  onChange={(e) => handleFieldChange('passwordAnswer', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="mustChangePassword"
                checked={formData.mustChangePassword ?? false}
                onChange={(e) => handleFieldChange('mustChangePassword', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 rounded"
              />
              <label htmlFor="mustChangePassword" className="ml-2 block text-sm text-neutral-700">
                Must change password on next login
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => handleFieldChange('isActive', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-neutral-700">
                Active
              </label>
            </div>
          </div>
        )}

        {activeTab === 'personal' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-neutral-800 mb-4">Personal Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">First Name *</label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) => handleFieldChange('firstName', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={(e) => handleFieldChange('lastName', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Gender</label>
                <select
                  value={formData.gender || ''}
                  onChange={(e) => handleFieldChange('gender', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                  <option value="Prefer Not to Say">Prefer Not to Say</option>
                </select>
              </div>
              {formData.gender === 'Other' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Gender (Other)</label>
                  <input
                    type="text"
                    value={formData.genderOther || ''}
                    onChange={(e) => handleFieldChange('genderOther', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Birthday</label>
                <input
                  type="date"
                  value={formData.birthday || ''}
                  onChange={(e) => handleFieldChange('birthday', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => handleFieldChange('phone', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Address</label>
              <textarea
                value={formData.address || ''}
                onChange={(e) => handleFieldChange('address', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">PSA License</label>
                <input
                  type="text"
                  value={formData.psaLicense || ''}
                  onChange={(e) => handleFieldChange('psaLicense', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={formData.startDate || ''}
                  onChange={(e) => handleFieldChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" id="visa-details">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Visa Number</label>
                <input
                  type="text"
                  value={formData.visaNumber || ''}
                  onChange={(e) => handleFieldChange('visaNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Visa Expiry Date</label>
                <input
                  type="date"
                  value={formData.visaExpiryDate || ''}
                  onChange={(e) => handleFieldChange('visaExpiryDate', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Comment</label>
              <textarea
                value={formData.comment || ''}
                onChange={(e) => handleFieldChange('comment', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {activeTab === 'work' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-neutral-800 mb-4">Work Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Main Location *</label>
                <select
                  required
                  value={formData.mainLocationId || ''}
                  onChange={(e) => handleFieldChange('mainLocationId', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Location</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Main Position *</label>
                <select
                  required
                  value={formData.mainPositionId || ''}
                  onChange={(e) => handleFieldChange('mainPositionId', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Position</option>
                  {positions.map((pos) => (
                    <option key={pos.id} value={pos.id}>
                      {pos.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Payroll Department *</label>
                <select
                  required
                  value={formData.payrollDepartmentId || ''}
                  onChange={(e) => handleFieldChange('payrollDepartmentId', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Payroll Department</option>
                  {payrollDepartments.filter(dept => dept.isActive).map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Employment Type *</label>
                <select
                  required
                  value={formData.employmentType || ''}
                  onChange={(e) => handleFieldChange('employmentType', e.target.value || undefined)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Employment Type</option>
                  <option value="Permanent">Permanent</option>
                  <option value="Contract">Contract</option>
                  <option value="Agency">Agency</option>
                </select>
              </div>
            </div>

            <BasketSelect
              label="Sub Locations"
              availableItems={locations
                .filter(loc => !formData.mainLocationId || loc.id !== formData.mainLocationId)
                .map(loc => ({ id: loc.id, label: loc.name }))}
              selectedIds={formData.subLocationIds || []}
              onChange={(ids) => handleArrayFieldChange('subLocationIds', ids.map(id => id.toString()))}
              placeholder="Select locations..."
            />

            <BasketSelect
              label="Sub Positions"
              availableItems={positions
                .filter(pos => !formData.mainPositionId || pos.id !== formData.mainPositionId)
                .map(pos => ({ id: pos.id, label: pos.title }))}
              selectedIds={formData.subPositionIds || []}
              onChange={(ids) => handleArrayFieldChange('subPositionIds', ids.map(id => id.toString()))}
              placeholder="Select positions..."
            />

            <BasketSelect
              label="Schedules"
              availableItems={schedules.map(schedule => ({ id: schedule.id, label: schedule.name }))}
              selectedIds={formData.scheduleIds || []}
              onChange={(ids) => handleArrayFieldChange('scheduleIds', ids.map(id => id.toString()))}
              placeholder="Select schedules..."
            />

            <BasketSelect
              label="App Roles"
              required
              availableItems={roles.map(role => ({ id: role.id, label: role.name }))}
              selectedIds={formData.appRoleIds || []}
              onChange={(ids) => handleArrayFieldChange('appRoleIds', ids.map(id => id.toString()))}
              placeholder="Select roles..."
            />

            <div className="flex items-center">
              <input
                type="checkbox"
                id="allowMobileClockIn"
                checked={formData.allowMobileClockIn ?? true}
                onChange={(e) => handleFieldChange('allowMobileClockIn', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 rounded"
              />
              <label htmlFor="allowMobileClockIn" className="ml-2 block text-sm text-neutral-700">
                Allow mobile clock in
              </label>
            </div>
          </div>
        )}



        <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-neutral-200">
          <button
            type="button"
            onClick={() => navigate('/employees')}
            className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
      )}
    </div>
  );
};

// Licenses Tab Component
const LicensesTab: React.FC<{ licenses: EmployeeLicense[]; employeeId: number; onRefresh: () => void }> = ({ licenses, employeeId, onRefresh }) => {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editingLicense, setEditingLicense] = useState<EmployeeLicense | null>(null);
  const [localLicenses, setLocalLicenses] = useState<EmployeeLicense[]>(licenses);
  const [formData, setFormData] = useState<CreateEmployeeLicenseRequest>({
    employeeId,
    licenseNumber: '',
    expiryDate: '',
  });

  // Update local licenses when props change
  React.useEffect(() => {
    setLocalLicenses(licenses);
  }, [licenses]);

  const refreshLicenses = async () => {
    try {
      console.log('Refreshing licenses for employee:', employeeId);
      const refreshedLicenses = await employeeLicenseApi.getByEmployeeId(employeeId);
      console.log('Refreshed licenses:', refreshedLicenses);
      setLocalLicenses(refreshedLicenses);
      // Also call the parent refresh to update the main state
      await onRefresh();
    } catch (error) {
      console.error('Error refreshing licenses:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      console.log('Saving license with data:', formData);
      let result;
      if (editingLicense) {
        result = await employeeLicenseApi.update(editingLicense.id, { licenseNumber: formData.licenseNumber, expiryDate: formData.expiryDate });
        console.log('License updated:', result);
      } else {
        result = await employeeLicenseApi.create(formData);
        console.log('License created:', result);
      }
      setShowModal(false);
      setEditingLicense(null);
      setFormData({ employeeId, licenseNumber: '', expiryDate: '' });
      // Refresh licenses immediately
      await refreshLicenses();
      toast.success(editingLicense ? 'License updated successfully!' : 'License added successfully!');
      console.log('Licenses refreshed after save');
    } catch (error: any) {
      console.error('Error saving license:', error);
      console.error('Error details:', error?.response?.data);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save license. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleEdit = (license: EmployeeLicense) => {
    setEditingLicense(license);
    setFormData({ employeeId, licenseNumber: license.licenseNumber, expiryDate: license.expiryDate.split('T')[0] });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this license?')) {
      try {
        await employeeLicenseApi.delete(id);
        await refreshLicenses();
        toast.success('License deleted successfully!');
      } catch (error) {
        console.error('Error deleting license:', error);
        toast.error('Failed to delete license. Please try again.');
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-neutral-800">Licenses</h2>
        <button
          type="button"
          onClick={() => {
            setEditingLicense(null);
            setFormData({ employeeId, licenseNumber: '', expiryDate: '' });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add License
        </button>
      </div>

      {localLicenses.length === 0 ? (
        <p className="text-neutral-500">No licenses found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">License Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Expiry Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {localLicenses.map((license) => (
                <tr key={license.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{license.licenseNumber}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{new Date(license.expiryDate).toLocaleDateString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button onClick={() => handleEdit(license)} className="text-blue-600 hover:text-blue-900 mr-4">
                      <Edit className="h-4 w-4 inline" />
                    </button>
                    <button onClick={() => handleDelete(license.id)} className="text-red-600 hover:text-red-900">
                      <Trash2 className="h-4 w-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{editingLicense ? 'Edit License' : 'Add License'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">License Number *</label>
                <input
                  type="text"
                  required
                  value={formData.licenseNumber}
                  onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Expiry Date *</label>
                <input
                  type="date"
                  required
                  value={formData.expiryDate}
                  onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                  {editingLicense ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Pay Records Tab Component - REMOVED: Now using PayrollAndPayRecords component
// The PayRecordsTab and PayrollTab components have been removed and replaced with PayrollAndPayRecords component

// Feedback Tab Component
const FeedbackTab: React.FC<{ feedbackRecords: FeedbackRecord[]; employeeId: number; onRefresh: () => void }> = ({ feedbackRecords, employeeId, onRefresh }) => {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<FeedbackRecord | null>(null);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState<Array<{ name: string; data: string; type: string }>>([]);
  const [followUps, setFollowUps] = useState<Array<{ email: string; note: string }>>([]);
  const [formData, setFormData] = useState<CreateFeedbackRecordRequest>({
    employeeId,
    type: '',
    actionTaken: '',
    description: '',
    priority: 'Medium',
    status: 'Unresolved',
  });

  // Helper function to convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Handle file selection
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newAttachments = [...attachments];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const base64 = await fileToBase64(file);
        newAttachments.push({
          name: file.name,
          data: base64,
          type: file.type
        });
      } catch (error) {
        console.error('Error converting file to base64:', error);
        toast.error(`Failed to process file: ${file.name}`);
      }
    }
    setAttachments(newAttachments);
    // Clear the input
    e.target.value = '';
  };

  // Remove attachment
  const handleRemoveAttachment = (index: number) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    setAttachments(newAttachments);
  };

  // Follow up helpers
  const handleAddFollowUp = () => {
    setFollowUps([...followUps, { email: '', note: '' }]);
  };

  const handleFollowUpChange = (index: number, field: 'email' | 'note', value: string) => {
    setFollowUps((prev) =>
      prev.map((fu, i) => (i === index ? { ...fu, [field]: value } : fu))
    );
  };

  const handleRemoveFollowUp = (index: number) => {
    setFollowUps((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submission
    if (saving) {
      return;
    }
    
    try {
      setSaving(true);

      // Convert attachments and follow-ups to JSON strings
      const attachmentsJson = attachments.length > 0 ? JSON.stringify(attachments) : undefined;
      const followUpsJson = followUps.length > 0 ? JSON.stringify(followUps) : undefined;

      const submitData: CreateFeedbackRecordRequest = {
        ...formData,
        attachments: attachmentsJson,
        followupRecords: followUpsJson,
      };

      if (editingRecord) {
        await feedbackRecordApi.update(editingRecord.id, submitData);
      } else {
        await feedbackRecordApi.create(submitData);
      }
      setShowModal(false);
      setEditingRecord(null);
      setAttachments([]);
      setFollowUps([]);
      setFormData({ employeeId, type: '', actionTaken: '', description: '', priority: 'Medium', status: 'Unresolved' });
      await onRefresh();
      toast.success(editingRecord ? 'Feedback record updated successfully!' : 'Feedback record created successfully!');
    } catch (error: any) {
      console.error('Error saving feedback record:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save feedback record. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (record: FeedbackRecord) => {
    setEditingRecord(record);
    setFormData({
      employeeId,
      type: record.type,
      actionTaken: record.actionTaken,
      description: record.description,
      priority: record.priority,
      status: record.status,
    });
    // Parse existing attachments
    if (record.attachments) {
      try {
        const parsedAttachments = JSON.parse(record.attachments);
        setAttachments(Array.isArray(parsedAttachments) ? parsedAttachments : []);
      } catch {
        setAttachments([]);
      }
    } else {
      setAttachments([]);
    }

    // Parse existing follow up records
    if (record.followupRecords) {
      try {
        const parsedFollowUps = JSON.parse(record.followupRecords);
        setFollowUps(
          Array.isArray(parsedFollowUps)
            ? parsedFollowUps.map((fu: any) => ({
                email: fu.email || '',
                note: fu.note || '',
              }))
            : []
        );
      } catch {
        setFollowUps([]);
      }
    } else {
      setFollowUps([]);
    }

    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this feedback record?')) {
      try {
        await feedbackRecordApi.delete(id);
        await onRefresh();
        toast.success('Feedback record deleted successfully!');
      } catch (error) {
        console.error('Error deleting feedback record:', error);
        toast.error('Failed to delete feedback record. Please try again.');
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-neutral-800">Feedback Records</h2>
        <button
          type="button"
          onClick={() => {
            setEditingRecord(null);
            setAttachments([]);
            setFollowUps([]);
            setFormData({ employeeId, type: '', actionTaken: '', description: '', priority: 'Medium', status: 'Unresolved' });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Feedback
        </button>
      </div>

      {feedbackRecords.length === 0 ? (
        <p className="text-neutral-500">No feedback records found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Action Taken</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {feedbackRecords.map((record) => (
                <tr key={record.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.type}</td>
                  <td className="px-6 py-4 text-sm text-neutral-900">{record.actionTaken}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.priority}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.status}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{new Date(record.createdAt).toLocaleDateString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button onClick={() => handleEdit(record)} className="text-blue-600 hover:text-blue-900 mr-4">
                      <Edit className="h-4 w-4 inline" />
                    </button>
                    <button onClick={() => handleDelete(record.id)} className="text-red-600 hover:text-red-900">
                      <Trash2 className="h-4 w-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-2xl my-8 max-h-[80vh] overflow-y-auto">
            <div className="px-6 pt-6 pb-2 border-b border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900">
                {editingRecord ? 'Edit Feedback Record' : 'Add Feedback Record'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Type *</label>
                  <input
                    type="text"
                    required
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Action Taken *</label>
                  <select
                    required
                    value={formData.actionTaken}
                    onChange={(e) => setFormData({ ...formData, actionTaken: e.target.value })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Select Action</option>
                    <option value="1st Verbal Warning">1st Verbal Warning</option>
                    <option value="1st Written Warning">1st Written Warning</option>
                    <option value="2nd Verbal Warning">2nd Verbal Warning</option>
                    <option value="2nd Written Warning">2nd Written Warning</option>
                    <option value="3rd Verbal Warning">3rd Verbal Warning</option>
                    <option value="3rd Written Warning">3rd Written Warning</option>
                    <option value="No Action Taken">No Action Taken</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Description *</label>
                  <textarea
                    required
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Priority *</label>
                    <select
                      required
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Status *</label>
                    <select
                      required
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="Unresolved">Unresolved</option>
                      <option value="Resolved">Resolved</option>
                      <option value="In Progress">In Progress</option>
                    </select>
                  </div>
                </div>
              {/* Attachments */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Attachments</label>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className={`flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-md text-sm ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-neutral-50'}`}>
                        <Paperclip className="h-4 w-4" />
                        Add Files
                        <input
                          type="file"
                          multiple
                          onChange={handleFileChange}
                          className="hidden"
                          accept="*/*"
                          disabled={saving}
                        />
                      </label>
                    </div>
                    
                    {attachments.length > 0 && (
                      <div className="space-y-2 mt-2">
                        {attachments.map((attachment, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-neutral-50 border border-neutral-200 rounded-md">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <FileText className="h-4 w-4 text-neutral-500 flex-shrink-0" />
                              <span className="text-sm text-neutral-700 truncate" title={attachment.name}>{attachment.name}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {attachment.data && (
                                <a
                                  href={attachment.data}
                                  download={attachment.name}
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  title="Download"
                                >
                                  <FileText className="h-4 w-4" />
                                </a>
                              )}
                              <button
                                type="button"
                                onClick={() => handleRemoveAttachment(index)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Remove"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

              {/* Follow ups */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-neutral-700">Follow Up</label>
                    <button
                      type="button"
                      onClick={handleAddFollowUp}
                      disabled={saving}
                      className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      + Add Follow Up
                    </button>
                  </div>
                  {followUps.length === 0 && (
                    <p className="text-xs text-neutral-500">No follow ups added.</p>
                  )}
                  <div className="space-y-3 mt-2">
                    {followUps.map((fu, index) => (
                      <div
                        key={index}
                        className="border border-neutral-200 rounded-md p-3 space-y-2 bg-neutral-50"
                      >
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-medium text-neutral-600">
                            Follow Up #{index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveFollowUp(index)}
                            disabled={saving}
                            className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-neutral-700 mb-1">
                              Email Address
                            </label>
                            <input
                              type="email"
                              value={fu.email}
                              onChange={(e) =>
                                handleFollowUpChange(index, 'email', e.target.value)
                              }
                              disabled={saving}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-neutral-700 mb-1">
                              Note
                            </label>
                            <textarea
                              rows={2}
                              value={fu.note}
                              onChange={(e) =>
                                handleFollowUpChange(index, 'note', e.target.value)
                              }
                              disabled={saving}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              <div className="flex gap-3 justify-end pt-2 border-t border-neutral-200 mt-4">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  disabled={saving}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {saving ? 'Saving...' : (editingRecord ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Notes Tab Component
const NotesTab: React.FC<{ employeeNotes: EmployeeNote[]; employeeId: number; onRefresh: () => void }> = ({ employeeNotes, employeeId, onRefresh }) => {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editingNote, setEditingNote] = useState<EmployeeNote | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<CreateEmployeeNoteRequest>({
    employeeId,
    noteDate: new Date().toISOString().split('T')[0], // Default to today
    subject: '',
    content: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (saving) {
      return;
    }
    
    try {
      setSaving(true);

      if (editingNote) {
        await employeeNoteApi.update(editingNote.id, formData);
      } else {
        await employeeNoteApi.create(formData);
      }
      setShowModal(false);
      setEditingNote(null);
      setFormData({ employeeId, noteDate: new Date().toISOString().split('T')[0], subject: '', content: '' });
      await onRefresh();
      toast.success(editingNote ? 'Note updated successfully!' : 'Note created successfully!');
    } catch (error: any) {
      console.error('Error saving note:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save note. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (note: EmployeeNote) => {
    setEditingNote(note);
    setFormData({
      employeeId,
      noteDate: note.noteDate.split('T')[0],
      subject: note.subject || '',
      content: note.content || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await employeeNoteApi.delete(id);
        await onRefresh();
        toast.success('Note deleted successfully!');
      } catch (error) {
        console.error('Error deleting note:', error);
        toast.error('Failed to delete note. Please try again.');
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-neutral-800">Notes</h2>
        <button
          type="button"
          onClick={() => {
            setEditingNote(null);
            setFormData({ employeeId, noteDate: new Date().toISOString().split('T')[0], subject: '', content: '' });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Note
        </button>
      </div>

      {employeeNotes.length === 0 ? (
        <p className="text-neutral-500">No notes found.</p>
      ) : (
        <div className="space-y-3">
          {employeeNotes.map((note) => (
            <div key={note.id} className="border border-neutral-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-medium text-neutral-600">
                      {formatDate(note.noteDate)}
                    </span>
                    {note.subject && (
                      <span className="text-sm font-semibold text-neutral-800">
                        {note.subject}
                      </span>
                    )}
                  </div>
                  {note.content && (
                    <p className="text-sm text-neutral-700 whitespace-pre-wrap">
                      {note.content}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    type="button"
                    onClick={() => handleEdit(note)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Edit note"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(note.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Delete note"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal for Add/Edit Note */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-neutral-800">
                {editingNote ? 'Edit Note' : 'Add Note'}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingNote(null);
                  setFormData({ employeeId, noteDate: new Date().toISOString().split('T')[0], subject: '', content: '' });
                }}
                className="text-neutral-500 hover:text-neutral-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.noteDate}
                  onChange={(e) => setFormData({ ...formData, noteDate: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={formData.subject || ''}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter note subject (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Content
                </label>
                <textarea
                  value={formData.content || ''}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={8}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter note content (optional)"
                />
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-neutral-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingNote(null);
                    setFormData({ employeeId, noteDate: new Date().toISOString().split('T')[0], subject: '', content: '' });
                  }}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={saving}
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingNote ? 'Update' : 'Create'} Note
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Training Tab Component
const TrainingTab: React.FC<{ trainingRecords: TrainingRecord[]; employeeId: number; trainingTypes: TrainingType[]; onRefresh: () => void }> = ({ trainingRecords, employeeId, trainingTypes, onRefresh }) => {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<TrainingRecord | null>(null);
  const [attachments, setAttachments] = useState<Array<{ name: string; data: string; type: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState<'Pending' | 'Completed' | 'Unassigned'>('Pending');
  const [formData, setFormData] = useState<CreateTrainingRecordRequest>({
    employeeId,
    trainingTypeId: 0,
    status: 'Pending',
  });

  // Derived lists for sub-tabs
  const pendingStatuses = ['Pending', 'In Progress', 'Overdue'];
  const pendingRecords = trainingRecords.filter((r) =>
    pendingStatuses.map((s) => s.toLowerCase()).includes(r.status.toLowerCase())
  );
  const completedRecords = trainingRecords.filter(
    (r) => r.status.toLowerCase() === 'completed'.toLowerCase()
  );

  const usedTrainingTypeIds = new Set<number>(
    trainingRecords.map((r) => r.trainingTypeId).filter((id) => !!id)
  );
  const unassignedTrainingTypes = trainingTypes.filter(
    (tt) => !usedTrainingTypeIds.has(tt.id)
  );

  // Helper function to convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Handle file selection
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newAttachments = [...attachments];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const base64 = await fileToBase64(file);
        newAttachments.push({
          name: file.name,
          data: base64,
          type: file.type
        });
      } catch (error) {
        console.error('Error converting file to base64:', error);
        toast.error(`Failed to process file: ${file.name}`);
      }
    }
    setAttachments(newAttachments);
    // Clear the input
    e.target.value = '';
  };

  // Remove attachment
  const handleRemoveAttachment = (index: number) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    setAttachments(newAttachments);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submission
    if (saving) {
      return;
    }
    
    try {
      if (!formData.trainingTypeId || formData.trainingTypeId === 0) {
        toast.warning('Please select a training type.');
        return;
      }
      
      setSaving(true);
      
      // Convert attachments to JSON string
      const attachmentsJson = attachments.length > 0 ? JSON.stringify(attachments) : undefined;
      const submitData = {
        ...formData,
        attachments: attachmentsJson
      };
      
      console.log('Saving training record with data:', submitData);
      let result;
      if (editingRecord) {
        result = await trainingRecordApi.update(editingRecord.id, submitData);
        console.log('Training record updated:', result);
      } else {
        result = await trainingRecordApi.create(submitData);
        console.log('Training record created:', result);
      }
      setShowModal(false);
      setEditingRecord(null);
      setAttachments([]);
      setFormData({ employeeId, trainingTypeId: 0, status: 'Pending' });
      await onRefresh();
      toast.success(editingRecord ? 'Training record updated successfully!' : 'Training record created successfully!');
      console.log('Training records refreshed after save');
    } catch (error: any) {
      console.error('Error saving training record:', error);
      console.error('Error details:', error?.response?.data);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save training record. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (record: TrainingRecord) => {
    setEditingRecord(record);
    setFormData({
      employeeId,
      trainingTypeId: record.trainingTypeId,
      comment: record.comment || '',
      dueDate: record.dueDate ? record.dueDate.split('T')[0] : '',
      completionDate: record.completionDate ? record.completionDate.split('T')[0] : '',
      status: record.status,
    });
    
    // Parse existing attachments
    if (record.attachments) {
      try {
        const parsedAttachments = JSON.parse(record.attachments);
        setAttachments(Array.isArray(parsedAttachments) ? parsedAttachments : []);
      } catch {
        setAttachments([]);
      }
    } else {
      setAttachments([]);
    }
    
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this training record?')) {
      try {
        await trainingRecordApi.delete(id);
        await onRefresh();
        toast.success('Training record deleted successfully!');
      } catch (error) {
        console.error('Error deleting training record:', error);
        toast.error('Failed to delete training record. Please try again.');
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-neutral-800">Training Records</h2>
        <button
          type="button"
          onClick={() => {
            setActiveSubTab('Pending');
            setEditingRecord(null);
            setAttachments([]);
            setFormData({ employeeId, trainingTypeId: 0, status: 'Pending' });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Training
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-neutral-200 mb-4">
        {(['Pending', 'Completed', 'Unassigned'] as const).map((tab) => {
          let count = 0;
          if (tab === 'Pending') count = pendingRecords.length;
          if (tab === 'Completed') count = completedRecords.length;
          if (tab === 'Unassigned') count = unassignedTrainingTypes.length;

          const label =
            tab === 'Unassigned'
              ? `Unassigned Training Types (${count})`
              : `${tab} (${count})`;

          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveSubTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                activeSubTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Pending / In Progress / Overdue */}
      {activeSubTab === 'Pending' && (
        <>
          {pendingRecords.length === 0 ? (
            <p className="text-neutral-500">
              No pending, in-progress, or overdue training records found.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Training Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Completed Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {pendingRecords.map((record) => (
                    <tr key={record.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {record.trainingType?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {record.dueDate ? new Date(record.dueDate).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {record.completionDate
                          ? new Date(record.completionDate).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {record.status}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEdit(record)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          <Edit className="h-4 w-4 inline" />
                        </button>
                        <button
                          onClick={() => handleDelete(record.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4 inline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Completed */}
      {activeSubTab === 'Completed' && (
        <>
          {completedRecords.length === 0 ? (
            <p className="text-neutral-500">No completed training records found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Training Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Completed Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {completedRecords.map((record) => (
                    <tr key={record.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {record.trainingType?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {record.dueDate ? new Date(record.dueDate).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {record.completionDate
                          ? new Date(record.completionDate).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {record.status}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEdit(record)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          <Edit className="h-4 w-4 inline" />
                        </button>
                        <button
                          onClick={() => handleDelete(record.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4 inline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Unassigned Training Types */}
      {activeSubTab === 'Unassigned' && (
        <>
          {unassignedTrainingTypes.length === 0 ? (
            <p className="text-neutral-500">
              All training types have at least one training record for this employee.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Training Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Frequency
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {unassignedTrainingTypes.map((type) => (
                    <tr key={type.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {type.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-neutral-900">
                        {type.description || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {type.frequency === 'One-Time'
                          ? 'One-Time'
                          : `${type.recurringValue ?? ''} ${type.recurringUnit ?? ''}`.trim()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8">
            <h3 className="text-lg font-semibold mb-4">{editingRecord ? 'Edit Training Record' : 'Add Training Record'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Training Type *</label>
                <select
                  required
                  value={formData.trainingTypeId}
                  onChange={(e) => setFormData({ ...formData, trainingTypeId: parseInt(e.target.value) })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value={0}>Select Training Type</option>
                  {trainingTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Comment</label>
                <textarea
                  rows={4}
                  value={formData.comment || ''}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={formData.dueDate || ''}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Status *</label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Overdue">Overdue</option>
                  </select>
                </div>
              </div>
              {formData.status === 'Completed' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Completed Date</label>
                  <input
                    type="date"
                    value={formData.completionDate || ''}
                    onChange={(e) => setFormData({ ...formData, completionDate: e.target.value })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Attachments</label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <label className={`flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-md text-sm ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-neutral-50'}`}>
                      <Paperclip className="h-4 w-4" />
                      Add Files
                      <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                        accept="*/*"
                        disabled={saving}
                      />
                    </label>
                  </div>
                  
                  {attachments.length > 0 && (
                    <div className="space-y-2 mt-2">
                      {attachments.map((attachment, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-neutral-50 border border-neutral-200 rounded-md">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <FileText className="h-4 w-4 text-neutral-500 flex-shrink-0" />
                            <span className="text-sm text-neutral-700 truncate" title={attachment.name}>{attachment.name}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {attachment.data && (
                              <a
                                href={attachment.data}
                                download={attachment.name}
                                className="text-blue-600 hover:text-blue-800 p-1"
                                title="Download"
                              >
                                <FileText className="h-4 w-4" />
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveAttachment(index)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Remove"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex gap-3 justify-end">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  disabled={saving}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {saving ? 'Saving...' : (editingRecord ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeProfile;

